import { Animate, Card, Checkbox, Progress, Row, useAPI } from "components/lib";
import { useMemo, useEffect, useState, useContext, useRef } from "react";
import { Bar } from "react-chartjs-2";
import { format, startOfMonth, endOfMonth, subDays } from "date-fns";
import { ViewContext } from "components/lib";

const labels = ["May 01", "May 15", "May 31", "Jun 10", "Jun 25"];

// Color scheme for API keys when multiple keys are selected
const MAX_SELECTED_KEYS = 3;

const API_KEY_COLORS = [
  "rgb(91 147 255)",   // Blue
  "rgb(255 214 107)",  // Yellow
  "rgb(53 150 14)",    // Green
  "rgb(255 99 132)",   // Red
  "rgb(54 162 235)",   // Light Blue
  "rgb(255 205 86)",   // Orange
  "rgb(75 192 192)",   // Teal
  "rgb(153 102 255)",  // Purple
  "rgb(255 159 64)",   // Orange-Red
  "rgb(201 203 207)",  // Gray
  "rgb(102 187 106)",  // Light Green
  "rgb(233 30 99)",    // Pink
  "rgb(156 39 176)",   // Deep Purple
  "rgb(0 150 136)",    // Teal
  "rgb(255 87 34)",    // Deep Orange
  "rgb(121 85 72)",    // Brown
  "rgb(96 125 139)",   // Blue Gray
  "rgb(244 67 54)",    // Red
  "rgb(233 30 99)",    // Pink
  "rgb(156 39 176)",   // Purple
  "rgb(103 58 183)",   // Deep Purple
  "rgb(63 81 181)",    // Indigo
  "rgb(33 150 243)",   // Blue
  "rgb(3 169 244)",    // Light Blue
  "rgb(0 188 212)",    // Cyan
  "rgb(0 150 136)",    // Teal
  "rgb(76 175 80)",    // Green
  "rgb(139 195 74)",   // Light Green
  "rgb(205 220 57)",   // Lime
  "rgb(255 235 59)",   // Yellow
  "rgb(255 193 7)",    // Amber
  "rgb(255 152 0)",    // Orange
  "rgb(255 87 34)",    // Deep Orange
  "rgb(121 85 72)",    // Brown
  "rgb(158 158 158)",  // Gray
  "rgb(96 125 139)"    // Blue Gray
];

export const requestApiData = {
  labels,
  datasets: [
    {
      fill: false,
      label: "Map API: 182/day",
      data: [50, 30, 60, 40, 50, 30, 80], // Your static values
      borderColor: "rgb(91 147 255)",
      backgroundColor: "rgb(91 147 255)",
      barThickness: 18,
    },
  ],
};

export const searchApiData = {
  labels,
  datasets: [
    {
      fill: false,
      label: "Search API: 25/day",
      data: [15, 60, 40, 90, 20, 70, 10], // Your static values
      borderColor: "rgb(255, 214, 107)",
      backgroundColor: "rgb(255, 214, 107)",
      barThickness: 18,
    },
  ],
};

// Add this function to calculate pricing based on usage tiers
const calculatePricingMaps = (usage) => {
  if (!usage || usage === 0) return 0;
  
  // Define pricing tiers
  const tiers = [
    { threshold: 0, rate: 0 },      // First 10,000 requests are free
    { threshold: 10000, rate: 0.0035 }, // 10,001-50,000: $0.01 per request
    { threshold: 100000, rate: 0.0028 }, // 50,001-100,000: $0.008 per request
    { threshold: 500000, rate: 0.0021}, // 100,001-500,000: $0.005 per request
    { threshold: 1000000, rate: 0.0012 }  // 500,001+: $0.003 per request
  ];
  
  let totalCost = 0;
  
  // Calculate cost for each tier
  for (let i = 0; i < tiers.length; i++) {
    const currentTier = tiers[i];
    const nextTier = tiers[i + 1] || { threshold: Infinity };
    
    // If usage is within this tier
    if (usage > currentTier.threshold) {
      // Calculate the amount in this tier
      const tierAmount = Math.min(usage, nextTier.threshold) - currentTier.threshold;
      // Add the cost for this tier
      totalCost += tierAmount * currentTier.rate;
    }
  }
  
  return totalCost;
};
const calculatePricingAutoComplete = (usage) => {
  if (!usage || usage === 0) return 0;
  
  // Define pricing tiers

  const tiers = [
    { threshold: 0, rate: 0 },      // First 25,000 requests are free
    { threshold: 10000, rate: 0.001415 }, // 25,001-100,000: $0.0017 per request
    { threshold: 100000, rate: 0.001135}, // 100,001-500,000: $0.0015 per request
    { threshold: 500000, rate: 0.00085}  // 500,001+: $0.00125 per request
  ];
  let totalCost = 0;
  
  // Calculate cost for each tier
  for (let i = 0; i < tiers.length; i++) {
    const currentTier = tiers[i];
    const nextTier = tiers[i + 1] || { threshold: Infinity };
    
    // If usage is within this tier
    if (usage > currentTier.threshold) {
      // Calculate the amount in this tier
      const tierAmount = Math.min(usage, nextTier.threshold) - currentTier.threshold;
      // Add the cost for this tier
      totalCost += tierAmount * currentTier.rate;
    }
  }
  
  return totalCost;
};
// Format the pricing display
const formatPricingMaps = (usage) => {
  const cost = calculatePricingMaps(usage);
  return cost === 0 ? "Free" : "$" + cost.toFixed(2);
};
const formatPricingSearch = (usage) => {
  const cost = calculatePricingAutoComplete(usage);
  return cost === 0 ? "Free" : "$" + cost.toFixed(2);
};
const calculateProgressMaps = (usage) => {
  if (!usage || usage === 0) return 0;
  
  // Free tier is 0-10,000
  const FREE_TIER = 10000;
  
  // If usage is within the free tier, calculate percentage
  if (usage <= FREE_TIER) {
    return (usage / FREE_TIER) * 100;
  }
  
  // If usage is above free tier, show 100% filled
  return 100;
};
// Add this function to calculate the progress percentage based on usage
const calculateProgressAutocomplete = (usage) => {
  if (!usage || usage === 0) return 0;
  
  // Free tier is 0-10,000
  const FREE_TIER = 10000;
  
  // If usage is within the free tier, calculate percentage
  if (usage <= FREE_TIER) {
    return (usage / FREE_TIER) * 100;
  }
  
  // If usage is above free tier, show 100% filled
  return 100;
};

// Add a new pricing calculation function for Geocode API
const calculatePricingSearch = (usage) => {
  if (!usage || usage === 0) return 0;
  
  // Define pricing tiers for Geocode API
  const tiers = [
    { threshold: 0, rate: 0 },      // First 10,000 requests are free
    { threshold: 25000, rate: 0.0017 }, // 10,001-50,000: $0.01 per request
    { threshold: 100000, rate: 0.0015 }, // 50,001-100,000: $0.008 per request
    { threshold: 500000, rate: 0.00125}, // 100,001-500,000: $0.005 per request
  ];
  
  let totalCost = 0;
  
  // Calculate cost for each tier
  for (let i = 0; i < tiers.length; i++) {
    const currentTier = tiers[i];
    const nextTier = tiers[i + 1] || { threshold: Infinity };
    
    // If usage is within this tier
    if (usage > currentTier.threshold) {
      // Calculate the amount in this tier
      const tierAmount = Math.min(usage, nextTier.threshold) - currentTier.threshold;
      // Add the cost for this tier
      totalCost += tierAmount * currentTier.rate;
    }
  }
  
  return totalCost;
};

// Update the progress calculation function for Geocode API
const calculateProgress3 = (usage) => {
  if (!usage || usage === 0) return 0;
  
  // Free tier is 0-25,000
  const FREE_TIER = 25000;
  
  // If usage is within the free tier, calculate percentage
  if (usage <= FREE_TIER) {
    return (usage / FREE_TIER) * 100;
  }
  
  // If usage is above free tier, show 100% filled
  return 100;
};

 // Add a new formatting function for Geocode API
 const formatPricing3 = (usage) => {
   const cost = calculatePricingSearch(usage);
   return cost === 0 ? "Free" : "$" + cost.toFixed(2);
 };

 // Add pricing calculation function for Directions API
 const calculatePricingDirections = (usage) => {
   if (!usage || usage === 0) return 0;
   
   // Define pricing tiers for Directions API
   const tiers = [
     { threshold: 0, rate: 0 },
     { threshold: 100000, rate: 0.0018 },
     { threshold: 500000, rate: 0.0014 },
     { threshold: 1000000, rate: 0.00108 }
   ];
   
   let totalCost = 0;
   
   // Calculate cost for each tier
   for (let i = 0; i < tiers.length; i++) {
     const currentTier = tiers[i];
     const nextTier = tiers[i + 1] || { threshold: Infinity };
     
     // If usage is within this tier
     if (usage > currentTier.threshold) {
       // Calculate the amount in this tier
       const tierAmount = Math.min(usage, nextTier.threshold) - currentTier.threshold;
       // Add the cost for this tier
       totalCost += tierAmount * currentTier.rate;
     }
   }
   
   return totalCost;
 };

 // Add progress calculation function for Directions API
 const calculateProgressDirections = (usage) => {
   if (!usage || usage === 0) return 0;
   
   // Free tier is 0-10,000
   const FREE_TIER = 100000;
   
   // If usage is within the free tier, calculate percentage
   if (usage <= FREE_TIER) {
     return (usage / FREE_TIER) * 100;
   }
   
   // If usage is above free tier, show 100% filled
   return 100;
 };

 // Add formatting function for Directions API
 const formatPricingDirections = (usage) => {
   const cost = calculatePricingDirections(usage);
   return cost === 0 ? "Free" : "$" + cost.toFixed(2);
 };

 // Add pricing calculation functions for remaining API types
 const calculatePricingMapMatching = (usage) => {
   if (!usage || usage === 0) return 0;
   const tiers = [
     { threshold: 0, rate: 0 },
     { threshold: 100000, rate: 0.0018 },
     { threshold: 500000, rate: 0.0014 },
     { threshold: 1000000, rate: 0.00108 }
   ];
   let totalCost = 0;
   for (let i = 0; i < tiers.length; i++) {
     const currentTier = tiers[i];
     const nextTier = tiers[i + 1] || { threshold: Infinity };
     if (usage > currentTier.threshold) {
       const tierAmount = Math.min(usage, nextTier.threshold) - currentTier.threshold;
       totalCost += tierAmount * currentTier.rate;
     }
   }
   return totalCost;
 };

 const calculatePricingOptimize = (usage) => {
   if (!usage || usage === 0) return 0;
   const tiers = [
    { threshold: 0, rate: 0 },
    { threshold: 100000, rate: 0.0018 },
    { threshold: 500000, rate: 0.0014 },
    { threshold: 1000000, rate: 0.00108 }
   ];
   let totalCost = 0;
   for (let i = 0; i < tiers.length; i++) {
     const currentTier = tiers[i];
     const nextTier = tiers[i + 1] || { threshold: Infinity };
     if (usage > currentTier.threshold) {
       const tierAmount = Math.min(usage, nextTier.threshold) - currentTier.threshold;
       totalCost += tierAmount * currentTier.rate;
     }
   }
   return totalCost;
 };

 const calculatePricingMatrix = (usage) => {
   if (!usage || usage === 0) return 0;
   const tiers = [
    { threshold: 0, rate: 0 },
    { threshold: 100000, rate: 0.0018 },
    { threshold: 500000, rate: 0.0014 },
    { threshold: 1000000, rate: 0.00108 }
   ];
   let totalCost = 0;
   for (let i = 0; i < tiers.length; i++) {
     const currentTier = tiers[i];
     const nextTier = tiers[i + 1] || { threshold: Infinity };
     if (usage > currentTier.threshold) {
       const tierAmount = Math.min(usage, nextTier.threshold) - currentTier.threshold;
       totalCost += tierAmount * currentTier.rate;
     }
   }
   return totalCost;
 };

 const calculatePricingIsochrone = (usage) => {
   if (!usage || usage === 0) return 0;
   const tiers = [
    { threshold: 0, rate: 0 },
    { threshold: 100000, rate: 0.0018 },
    { threshold: 500000, rate: 0.0014 },
    { threshold: 1000000, rate: 0.00108 }
   ];
   let totalCost = 0;
   for (let i = 0; i < tiers.length; i++) {
     const currentTier = tiers[i];
     const nextTier = tiers[i + 1] || { threshold: Infinity };
     if (usage > currentTier.threshold) {
       const tierAmount = Math.min(usage, nextTier.threshold) - currentTier.threshold;
       totalCost += tierAmount * currentTier.rate;
     }
   }
   return totalCost;
 };

 const calculatePricingElevation = (usage) => {
   if (!usage || usage === 0) return 0;
   const tiers = [
    { threshold: 0, rate: 0 },
    { threshold: 100000, rate: 0.0018 },
    { threshold: 500000, rate: 0.0014 },
    { threshold: 1000000, rate: 0.00108 }
   ];
   let totalCost = 0;
   for (let i = 0; i < tiers.length; i++) {
     const currentTier = tiers[i];
     const nextTier = tiers[i + 1] || { threshold: Infinity };
     if (usage > currentTier.threshold) {
       const tierAmount = Math.min(usage, nextTier.threshold) - currentTier.threshold;
       totalCost += tierAmount * currentTier.rate;
     }
   }
   return totalCost;
 };

 const calculatePricingNavigation = (usage) => {
   if (!usage || usage === 0) return 0;
   // Navigation SDK sessions: the Stripe price on navigation_sdk_meter (graduated, monthly).
   const tiers = [
     { threshold: 0, rate: 0 },
     { threshold: 1000, rate: 0.0125 },
     { threshold: 100000, rate: 0.01 },
     { threshold: 500000, rate: 0.0075 },
     { threshold: 1000000, rate: 0.004 },
     { threshold: 5000000, rate: 0.001 }
   ];
   let totalCost = 0;
   for (let i = 0; i < tiers.length; i++) {
     const currentTier = tiers[i];
     const nextTier = tiers[i + 1] || { threshold: Infinity };
     if (usage > currentTier.threshold) {
       const tierAmount = Math.min(usage, nextTier.threshold) - currentTier.threshold;
       totalCost += tierAmount * currentTier.rate;
     }
   }
   return totalCost;
 };

 const calculatePricingTiles = (usage) => {
   if (!usage || usage === 0) return 0;
   const tiers = [
     { threshold: 0, rate: 0 },
     { threshold: 10000, rate: 0.001 },
     { threshold: 100000, rate: 0.0008 },
     { threshold: 500000, rate: 0.0006 }
   ];
   let totalCost = 0;
   for (let i = 0; i < tiers.length; i++) {
     const currentTier = tiers[i];
     const nextTier = tiers[i + 1] || { threshold: Infinity };
     if (usage > currentTier.threshold) {
       const tierAmount = Math.min(usage, nextTier.threshold) - currentTier.threshold;
       totalCost += tierAmount * currentTier.rate;
     }
   }
   return totalCost;
 };

 const calculatePricingStaticImages = (usage) => {
   if (!usage || usage === 0) return 0;
   const tiers = [
     { threshold: 0, rate: 0 },
     { threshold: 10000, rate: 0.002 },
     { threshold: 100000, rate: 0.0015 },
     { threshold: 500000, rate: 0.001 }
   ];
   let totalCost = 0;
   for (let i = 0; i < tiers.length; i++) {
     const currentTier = tiers[i];
     const nextTier = tiers[i + 1] || { threshold: Infinity };
     if (usage > currentTier.threshold) {
       const tierAmount = Math.min(usage, nextTier.threshold) - currentTier.threshold;
       totalCost += tierAmount * currentTier.rate;
     }
   }
   return totalCost;
 };

 // Progress calculation functions for remaining API types
 const calculateProgressMapMatching = (usage) => {
   if (!usage || usage === 0) return 0;
   const FREE_TIER = 100000;
   if (usage <= FREE_TIER) {
     return (usage / FREE_TIER) * 100;
   }
   return 100;
 };

 const calculateProgressOptimize = (usage) => {
   if (!usage || usage === 0) return 0;
   const FREE_TIER = 100000;
   if (usage <= FREE_TIER) {
     return (usage / FREE_TIER) * 100;
   }
   return 100;
 };

 const calculateProgressMatrix = (usage) => {
   if (!usage || usage === 0) return 0;
   const FREE_TIER = 100000;
   if (usage <= FREE_TIER) {
     return (usage / FREE_TIER) * 100;
   }
   return 100;
 };

 const calculateProgressIsochrone = (usage) => {
   if (!usage || usage === 0) return 0;
   const FREE_TIER = 100000;
   if (usage <= FREE_TIER) {
     return (usage / FREE_TIER) * 100;
   }
   return 100;
 };

 const calculateProgressElevation = (usage) => {
   if (!usage || usage === 0) return 0;
   const FREE_TIER = 100000;
   if (usage <= FREE_TIER) {
     return (usage / FREE_TIER) * 100;
   }
   return 100;
 };

 const calculateProgressNavigation = (usage) => {
   if (!usage || usage === 0) return 0;
   const FREE_TIER = 1000;
   if (usage <= FREE_TIER) {
     return (usage / FREE_TIER) * 100;
   }
   return 100;
 };

 const calculateProgressTiles = (usage) => {
   if (!usage || usage === 0) return 0;
   const FREE_TIER = 10000;
   if (usage <= FREE_TIER) {
     return (usage / FREE_TIER) * 100;
   }
   return 100;
 };

 const calculateProgressStaticImages = (usage) => {
   if (!usage || usage === 0) return 0;
   const FREE_TIER = 10000;
   if (usage <= FREE_TIER) {
     return (usage / FREE_TIER) * 100;
   }
   return 100;
 };

 // Formatting functions for remaining API types
 const formatPricingMapMatching = (usage) => {
   const cost = calculatePricingMapMatching(usage);
   return cost === 0 ? "Free" : "$" + cost.toFixed(2);
 };

 const formatPricingOptimize = (usage) => {
   const cost = calculatePricingOptimize(usage);
   return cost === 0 ? "Free" : "$" + cost.toFixed(2);
 };

 const formatPricingMatrix = (usage) => {
   const cost = calculatePricingMatrix(usage);
   return cost === 0 ? "Free" : "$" + cost.toFixed(2);
 };

 const formatPricingIsochrone = (usage) => {
   const cost = calculatePricingIsochrone(usage);
   return cost === 0 ? "Free" : "$" + cost.toFixed(2);
 };

 const formatPricingElevation = (usage) => {
   const cost = calculatePricingElevation(usage);
   return cost === 0 ? "Free" : "$" + cost.toFixed(2);
 };

 const formatPricingNavigation = (usage) => {
   const cost = calculatePricingNavigation(usage);
   return cost === 0 ? "Free" : "$" + cost.toFixed(2);
 };

 const formatPricingTiles = (usage) => {
   const cost = calculatePricingTiles(usage);
   return cost === 0 ? "Free" : "$" + cost.toFixed(2);
 };

 const formatPricingStaticImages = (usage) => {
  const cost = calculatePricingStaticImages(usage);
  return cost === 0 ? "Free" : "$" + cost.toFixed(2);
};

// Function to apply colors to datasets when multiple API keys are selected
const applyApiKeyColors = (usageData, selectedApiKeys) => {
  console.log('applyApiKeyColors called with:', { usageData, selectedApiKeys });
  
  if (!usageData || !usageData.datasets || selectedApiKeys.length <= 1) {
    console.log('applyApiKeyColors: returning original data (no changes needed)');
    return usageData;
  }

  // Create a consistent color assignment based only on API key name
  const apiKeyColorMap = {};
  const usedColors = new Set();
  
  selectedApiKeys.forEach((apiKey) => {
    // Generate a hash based ONLY on the API key name for consistent colors across all charts
    let hash = 0;
    for (let i = 0; i < apiKey.length; i++) {
      const char = apiKey.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    
    let colorIndex = Math.abs(hash) % API_KEY_COLORS.length;
    let color = API_KEY_COLORS[colorIndex];
    
    // If this color is already used, find the next available color
    let attempts = 0;
    while (usedColors.has(color) && attempts < API_KEY_COLORS.length) {
      colorIndex = (colorIndex + 1) % API_KEY_COLORS.length;
      color = API_KEY_COLORS[colorIndex];
      attempts++;
    }
    
    apiKeyColorMap[apiKey] = color;
    usedColors.add(color);
  });
  
  console.log('apiKeyColorMap:', apiKeyColorMap);

  // Create a copy of the data to avoid mutating the original
  const coloredData = {
    ...usageData,
    datasets: usageData.datasets.map((dataset, index) => {
      console.log(`Dataset ${index}:`, dataset.label);
      
      // Extract API key name from the dataset label
      // Label format is typically "API Key Name (Usage Type)" or just "API Key Name"
      let apiKeyName = dataset.label || '';
      
      // Handle format: "API Key Name (Usage Type)"
      if (apiKeyName.includes('(') && apiKeyName.includes(')')) {
        apiKeyName = apiKeyName.split('(')[0].trim();
      }
      // Handle format: "API Key Name: Usage Info"
      else if (apiKeyName.includes(':')) {
        apiKeyName = apiKeyName.split(':')[0].trim();
      }
      
      console.log(`Extracted API key name: "${apiKeyName}"`);
      
      // Find the matching API key from selectedApiKeys with more precise matching
      // Map API key IDs to names for proper matching
      const apiKeyIdToName = {
        '1': 'Production API Key',
        '2': 'Development Key', 
        '3': 'Testing Environment'
      };
      
      let matchingApiKey = null;
      
      // First try exact match by converting ID to name
      matchingApiKey = selectedApiKeys.find(key => {
        const keyName = apiKeyIdToName[String(key)];
        return apiKeyName === keyName;
      });
      
      // If no exact match, try partial match
      if (!matchingApiKey) {
        matchingApiKey = selectedApiKeys.find(key => {
          const keyName = apiKeyIdToName[String(key)];
          return keyName && (apiKeyName.includes(keyName) || keyName.includes(apiKeyName));
        });
      }
      
      // If still no match, try matching by the first part of the API key name
      if (!matchingApiKey) {
        const apiKeyFirstPart = apiKeyName.split(' ')[0]; // Get first word
        matchingApiKey = selectedApiKeys.find(key => 
          key.includes(apiKeyFirstPart) || apiKeyFirstPart.includes(key.split(' ')[0])
        );
      }
      
      console.log(`Matching API key: "${matchingApiKey}" for "${apiKeyName}"`);
      
      // Use the color for this API key, or fallback to a default color
      const color = matchingApiKey ? apiKeyColorMap[matchingApiKey] : API_KEY_COLORS[index % API_KEY_COLORS.length];
      
      console.log(`Assigned color: ${color}`);
      
      return {
        ...dataset,
        backgroundColor: color,
        borderColor: color,
      };
    })
  };

  console.log('Final colored data:', coloredData);
  return coloredData;
};

export function Analytics({ t }) {
  const [selectedValues, setSelectedValues] = useState([]);
  const [selectedApiKeys, setSelectedApiKeys] = useState([]);
  
  // State for unified chart API selection
  const [selectedViewApis, setSelectedViewApis] = useState(['maps', 'search', 'geocode', 'directions', 'mapmatching', 'optimize', 'matrix', 'isochrone', 'elevation', 'navigation']);
  
  const viewContext = useContext(ViewContext);
  // Get dateRange from viewContext (set by DashboardLayout)

  const dateRange = viewContext?.dateRange || {
    start: format(subDays(new Date(), 7), "yyyy-MM-dd"),
    end: format(new Date(), "yyyy-MM-dd")
  };
  
  console.log("Analytics dateRange:", dateRange);
  
  // Use a ref to store the previous dateRange values to detect changes
  const prevDateRangeRef = useRef({ start: dateRange.start, end: dateRange.end });
  
  // Add a trigger to force refetch when date range changes
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  
  // Effect to detect changes in dateRange or selectedApiKeys and trigger a reload
  useEffect(() => {
    // Compare current dateRange with previous
    if (prevDateRangeRef.current.start !== dateRange.start ||
        prevDateRangeRef.current.end !== dateRange.end) {
      console.log("Date range changed, refreshing data:", dateRange);
      setRefreshTrigger(prev => prev + 1);

      // Update the ref with current values
      prevDateRangeRef.current = { ...dateRange };
    }
  }, [dateRange.start, dateRange.end]);
  
  const keys = useAPI("/api/key/");

  // Get all API key IDs or selected ones
  const allKeyIds = keys.data?.map(key => key.id) || [];
  const apiKeysToUse = selectedApiKeys.length > 0 ? selectedApiKeys : allKeyIds;
  const apiKeysParam = apiKeysToUse.length > 0 ? `&apiKeysSet=${apiKeysToUse.join(',')}` : '';

  // Create the API URLs with comma-separated API keys and date range
  const mapsUsageUrl = `/api/stripe/usage?meter=maps${apiKeysParam}&startDate=${dateRange.start}&endDate=${dateRange.end}&_t=${refreshTrigger}`;
  const searchUsageUrl = `/api/stripe/usage?meter=search${apiKeysParam}&startDate=${dateRange.start}&endDate=${dateRange.end}&_t=${refreshTrigger}`;
  const geocodeUsageUrl = `/api/stripe/usage?meter=geocode${apiKeysParam}&startDate=${dateRange.start}&endDate=${dateRange.end}&_t=${refreshTrigger}`;
  const directionsUsageUrl = `/api/stripe/usage?meter=directions${apiKeysParam}&startDate=${dateRange.start}&endDate=${dateRange.end}&_t=${refreshTrigger}`;
  const mapMatchingUsageUrl = `/api/stripe/usage?meter=map_matching${apiKeysParam}&startDate=${dateRange.start}&endDate=${dateRange.end}&_t=${refreshTrigger}`;
  const optimizeUsageUrl = `/api/stripe/usage?meter=optimize${apiKeysParam}&startDate=${dateRange.start}&endDate=${dateRange.end}&_t=${refreshTrigger}`;
  const matrixUsageUrl = `/api/stripe/usage?meter=matrix${apiKeysParam}&startDate=${dateRange.start}&endDate=${dateRange.end}&_t=${refreshTrigger}`;
  const isochroneUsageUrl = `/api/stripe/usage?meter=isochrone${apiKeysParam}&startDate=${dateRange.start}&endDate=${dateRange.end}&_t=${refreshTrigger}`;
  const elevationUsageUrl = `/api/stripe/usage?meter=elevation${apiKeysParam}&startDate=${dateRange.start}&endDate=${dateRange.end}&_t=${refreshTrigger}`;
  const navigationUsageUrl = `/api/stripe/usage?meter=navigation${apiKeysParam}&startDate=${dateRange.start}&endDate=${dateRange.end}&_t=${refreshTrigger}`;
  const tilesUsageUrl = `/api/stripe/usage?meter=tiles${apiKeysParam}&startDate=${dateRange.start}&endDate=${dateRange.end}&_t=${refreshTrigger}`;
  const staticImagesUsageUrl = `/api/stripe/usage?meter=static_images${apiKeysParam}&startDate=${dateRange.start}&endDate=${dateRange.end}&_t=${refreshTrigger}`;

   // Force reload the API data when URLs change
   const mapsUsage = useAPI(mapsUsageUrl);
   const searchUsage = useAPI(searchUsageUrl);
   const geocodeUsage = useAPI(geocodeUsageUrl);
   const directionsUsage = useAPI(directionsUsageUrl);
   const mapMatchingUsage = useAPI(mapMatchingUsageUrl);
   const optimizeUsage = useAPI(optimizeUsageUrl);
   const matrixUsage = useAPI(matrixUsageUrl);
   const isochroneUsage = useAPI(isochroneUsageUrl);
   const elevationUsage = useAPI(elevationUsageUrl);
   const navigationUsage = useAPI(navigationUsageUrl);
   const tilesUsage = useAPI(tilesUsageUrl);
   const staticImagesUsage = useAPI(staticImagesUsageUrl);

  const [isDarkMode, setIsDarkMode] = useState(
    JSON.parse(localStorage.getItem("dark_mode")) || false
  );

  // Update state whenever localStorage changes
  useEffect(() => {
    const handleStorageChange = () => {
      setIsDarkMode(JSON.parse(localStorage.getItem("dark_mode")) || false);
    };

    window.addEventListener("storage", handleStorageChange);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
    };
  }, []);

  // Handle API key selection with 3-key limit
  const handleApiKeyChange = (value) => {
    console.log('handleApiKeyChange called with:', value);
    setSelectedApiKeys((prev) => {
      // If unchecking, always allow
      if (prev.includes(value)) {
        const newKeys = prev.filter(key => key !== value);
        console.log('Unchecking - Updated selectedApiKeys:', newKeys);
        return newKeys;
      }
      
      // If checking and already at max, don't allow
      if (prev.length >= MAX_SELECTED_KEYS) {
        console.log(`Max ${MAX_SELECTED_KEYS} keys reached, ignoring selection`);
        return prev;
      }
      
      // Add new key
      const newKeys = [...prev, value];
      console.log('Adding key - Updated selectedApiKeys:', newKeys);
      return newKeys;
    });
  };

  const chartOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: false, // Custom legend will be implemented separately
        },
      },
      layout: {
        padding: {
          top: 20,
          bottom: 30,
          left: 10,
          right: 10,
        },
      },
      scales: {
        x: {
          ticks: {
            color: "rgba(255, 255, 255, 0.7)",
            font: {
              size: window.innerWidth < 768 ? 10 : 12
            },
            maxRotation: 45,
            minRotation: 0,
            callback: function(value, index, ticks) {
              const label = this.getLabelForValue(value);
              if (!label) return label;
              
              // For long date ranges, show fewer labels to avoid overcrowding
              const totalTicks = ticks.length;
              if (totalTicks > 14) {
                // Show every 3rd tick for month view
                if (index % 3 !== 0 && index !== 0 && index !== totalTicks - 1) {
                  return '';
                }
              } else if (totalTicks > 7) {
                // Show every other tick for 2-week view
                if (index % 2 !== 0 && index !== totalTicks - 1) {
                  return '';
                }
              }
              
              // Extract the date from the formatted label (e.g., "Jan 8 2025, 3 PM")
              const match = label.match(/(\w+)\s+(\d+)\s+(\d{4})/);
              return match ? `${match[1]} ${match[2]}` : label;
            }
          },
          grid: {
            color: "rgba(255, 255, 255, 0.1)",
          },
        },
        y: {
          ticks: {
            color: "rgba(255, 255, 255, 0.7)",
            font: {
              size: window.innerWidth < 768 ? 10 : 12
            }
          },
          grid: {
            color: "rgba(255, 255, 255, 0.08)",
          },
        },
      },
    }),
    [isDarkMode]
  );

  // Prevent horizontal scroll on mount
  useEffect(() => {
    document.body.style.overflowX = 'hidden';
    document.documentElement.style.overflowX = 'hidden';
    return () => {
      document.body.style.overflowX = 'auto';
      document.documentElement.style.overflowX = 'auto';
    };
  }, []);

  // Helper to extract month/year from label
  const getMonthYear = (label) => {
    if (!label) return '';
    try {
      // If label is a timestamp, convert it first
      if (typeof label === 'number') {
        const date = new Date(label);
        return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
      }
      // If label is already formatted string like "May 15 2025, 3 PM"
      const match = label.match(/(\w+)\s+\d+\s+(\d{4})/);
      return match ? `${match[1]} ${match[2]}` : '';
    } catch (e) {
      return '';
    }
  };

  // Plugin for rounded corners
  const roundedCornersPlugin = {
    id: "roundedCorners",
    afterDatasetsDraw(chart) {
      const { ctx, data, scales, chartArea } = chart;
      const xScale = scales.x;
      const yScale = scales.y;
      const datasets = data.datasets;

      ctx.save();

      datasets.forEach((dataset, datasetIndex) => {
        const meta = chart.getDatasetMeta(datasetIndex);

        meta.data.forEach((bar, index) => {
          const barValue = dataset.data[index];
          let x = bar.x - bar.width / 2;
          const y = Math.min(bar.y, yScale.getPixelForValue(0));
          const height = Math.abs(bar.y - yScale.getPixelForValue(0));
          const radius = Math.min(10, height / 2);

          // Ensure bar doesn't extend beyond chartArea left boundary
          let barWidth = bar.width;
          if (x < chartArea.left) {
            barWidth = barWidth - (chartArea.left - x);
            x = chartArea.left;
          }

          // Ensure bar doesn't extend beyond chartArea right boundary
          if (x + barWidth > chartArea.right) {
            barWidth = chartArea.right - x;
          }

          // Skip if bar has no visible width
          if (barWidth <= 0 || height <= 0) {
            return;
          }

          // Rounded bar logic
          ctx.beginPath();
          ctx.fillStyle =
            dataset.backgroundColor instanceof Array
              ? dataset.backgroundColor[index]
              : dataset.backgroundColor;

          // Adjust radius if bar is too narrow
          const adjustedRadius = Math.min(radius, barWidth / 2);

          // Draw rectangle with rounded top corners
          ctx.moveTo(x + adjustedRadius, y);
          ctx.lineTo(x + barWidth - adjustedRadius, y);
          ctx.quadraticCurveTo(x + barWidth, y, x + barWidth, y + adjustedRadius);
          ctx.lineTo(x + barWidth, y + height);
          ctx.lineTo(x, y + height);
          ctx.lineTo(x, y + adjustedRadius);
          ctx.quadraticCurveTo(x, y, x + adjustedRadius, y);
          ctx.closePath();

          ctx.fill();
        });
      });

      ctx.restore();
    },
  };

  // Helper function to render custom glassmorphic legend
  const renderCustomLegend = (chartData) => {
    if (!chartData || !chartData.datasets || chartData.datasets.length === 0) {
      return null;
    }

    return (
      <div className="mt-4 flex flex-wrap gap-3 justify-center">
        {chartData.datasets.map((dataset, index) => (
          <div
            key={index}
            className="flex items-center gap-2 px-3 py-2 glass-card dark:glass-card-dark rounded-lg border border-white/10"
          >
            <div
              className="w-3 h-3 rounded-full"
              style={{
                backgroundColor: dataset.backgroundColor,
                boxShadow: `0 0 8px ${dataset.backgroundColor}`
              }}
            />
            <span className="text-white/80 text-sm font-light poppins-light">
              {dataset.label}
            </span>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="min-h-screen w-full overflow-x-hidden">
      <div className="py-3 w-full box-border px-6 overflow-x-hidden">
        <Animate type="pop">
          <div className="space-y-6">
        {/* Header Section */}
        <div className="glass-card dark:glass-card-dark backdrop-blur-lg border border-white/10 rounded-lg p-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold text-white poppins-medium">
                Analytics Dashboard
              </h1>
              <p className="text-white/60 poppins-light mt-1">
                Monitor your API usage across all services
              </p>
              <p className="text-xs text-white/60 poppins-light mt-2 italic">
                *This is an estimated cost only. Final charges may differ and are determined on the billing page.
              </p>
            </div>
            <div className="flex items-center gap-3">
              <div className="bg-white/20 px-3 py-1 rounded-full text-white/80 text-sm poppins-light">
                {selectedApiKeys.length > 0 ? `${selectedApiKeys.length} API Key${selectedApiKeys.length > 1 ? 's' : ''} selected` : 'All API Keys'}
              </div>
            </div>
          </div>
        </div>

        {/* Overview Section - 4 Summary Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-8">
          {/* Total Usage Card */}
          <div className="glass-card dark:glass-card-dark backdrop-blur-lg border border-white/10 rounded-lg p-4">
            <div className="flex items-center gap-3">
              <div className="flex items-center justify-center w-12 h-12 rounded-lg bg-blue-500/20 border border-blue-400/30">
                <div className="w-6 h-6 rounded bg-gradient-to-r from-blue-400 to-blue-600"></div>
              </div>
              <div>
                <div className="text-white/60 poppins-light text-sm">Total Usage</div>
                <div className="text-white poppins-medium text-lg font-semibold">
                  {(mapsUsage?.data?.totalUsage || 0) + 
                   (searchUsage?.data?.totalUsage || 0) + 
                   (geocodeUsage?.data?.totalUsage || 0) + 
                   (directionsUsage?.data?.totalUsage || 0) + 
                   (mapMatchingUsage?.data?.totalUsage || 0) + 
                   (optimizeUsage?.data?.totalUsage || 0) + 
                   (matrixUsage?.data?.totalUsage || 0) + 
                   (isochroneUsage?.data?.totalUsage || 0) + 
                   (isochroneUsage?.data?.totalUsage || 0) > 0 
                   ? ((mapsUsage?.data?.totalUsage || 0) + 
                      (searchUsage?.data?.totalUsage || 0) + 
                      (geocodeUsage?.data?.totalUsage || 0) + 
                      (directionsUsage?.data?.totalUsage || 0) + 
                      (mapMatchingUsage?.data?.totalUsage || 0) + 
                      (optimizeUsage?.data?.totalUsage || 0) + 
                      (matrixUsage?.data?.totalUsage || 0) + 
                      (isochroneUsage?.data?.totalUsage || 0)).toLocaleString()
                   : "Loading..."}
                </div>
              </div>
            </div>
          </div>
          
          {/* Total Cost Card */}
          <div className="glass-card dark:glass-card-dark backdrop-blur-lg border border-white/10 rounded-lg p-4">
            <div className="flex items-center gap-3">
              <div className="flex items-center justify-center w-12 h-12 rounded-lg bg-green-500/20 border border-green-400/30">
                <div className="w-6 h-6 rounded bg-gradient-to-r from-green-400 to-emerald-600"></div>
              </div>
              <div>
                <div className="text-white/60 poppins-light text-sm">Total Cost</div>
                <div className="text-white poppins-medium text-lg font-semibold">
                  ${((calculatePricingMaps(mapsUsage?.data?.totalUsage) || 0) + 
                     (calculatePricingSearch(searchUsage?.data?.totalUsage) || 0) + 
                     (calculatePricingSearch(geocodeUsage?.data?.totalUsage) || 0) + 
                     (calculatePricingDirections(directionsUsage?.data?.totalUsage) || 0) + 
                     (calculatePricingMapMatching(mapMatchingUsage?.data?.totalUsage) || 0) + 
                     (calculatePricingOptimize(optimizeUsage?.data?.totalUsage) || 0) + 
                     (calculatePricingMatrix(matrixUsage?.data?.totalUsage) || 0) + 
                     (calculatePricingIsochrone(isochroneUsage?.data?.totalUsage) || 0) + 
                     (calculatePricingIsochrone(isochroneUsage?.data?.totalUsage) || 0)).toFixed(2)}
                </div>
              </div>
            </div>
          </div>
          
          {/* Active APIs Card */}
          <div className="glass-card dark:glass-card-dark backdrop-blur-lg border border-white/10 rounded-lg p-4">
            <div className="flex items-center gap-3">
              <div className="flex items-center justify-center w-12 h-12 rounded-lg bg-yellow-500/20 border border-yellow-400/30">
                <div className="w-6 h-6 rounded bg-gradient-to-r from-yellow-400 to-orange-500"></div>
              </div>
              <div>
                <div className="text-white/60 poppins-light text-sm">Active APIs</div>
                <div className="text-white poppins-medium text-lg font-semibold">
                  {[mapsUsage?.data?.totalUsage, searchUsage?.data?.totalUsage, geocodeUsage?.data?.totalUsage, directionsUsage?.data?.totalUsage, mapMatchingUsage?.data?.totalUsage, optimizeUsage?.data?.totalUsage, matrixUsage?.data?.totalUsage, isochroneUsage?.data?.totalUsage].filter(usage => usage && usage > 0).length || "Loading..."}
                </div>
              </div>
            </div>
          </div>
          
          {/* Top API Card */}
          <div className="glass-card dark:glass-card-dark backdrop-blur-lg border border-white/10 rounded-lg p-4">
            <div className="flex items-center gap-3">
              <div className="flex items-center justify-center w-12 h-12 rounded-lg bg-purple-500/20 border border-purple-400/30">
                <div className="w-6 h-6 rounded bg-gradient-to-r from-purple-400 to-purple-600"></div>
              </div>
              <div>
                <div className="text-white/60 poppins-light text-sm">Top API</div>
                <div className="text-white poppins-medium text-lg font-semibold">
                  {(() => {
                    const usages = [
                      { name: 'Maps', usage: mapsUsage?.data?.totalUsage || 0 },
                      { name: 'Search', usage: searchUsage?.data?.totalUsage || 0 },
                      { name: 'Geocode', usage: geocodeUsage?.data?.totalUsage || 0 },
                      { name: 'Directions', usage: directionsUsage?.data?.totalUsage || 0 },
                      { name: 'Map Matching', usage: mapMatchingUsage?.data?.totalUsage || 0 },
                      { name: 'Optimize', usage: optimizeUsage?.data?.totalUsage || 0 },
                      { name: 'Matrix', usage: matrixUsage?.data?.totalUsage || 0 },
                      { name: 'Isochrone', usage: isochroneUsage?.data?.totalUsage || 0 },
                      { name: 'Navigation SDK', usage: navigationUsage?.data?.totalUsage || 0 },
                    ];
                    const topAPI = usages.reduce((max, api) => api.usage > max.usage ? api : max, { name: 'None', usage: 0 });
                    return topAPI.usage > 0 ? topAPI.name : 'Loading...';
                  })()} 
                </div>
              </div>
            </div>
          </div>
        </div>

        <Row className="flex flex-col-reverse 2xl:flex-row xl:flex-no-wrap gap-4 sm:gap-6 overflow-x-hidden">
          <div className="w-full 2xl:w-9/12 overflow-x-hidden min-w-0">
            {/* Unified API Analytics */}
            <div className="mb-8">
              <div className="flex items-center gap-3 mb-6">
                <h2 className="text-xl font-semibold text-white poppins-medium">API Usage Analytics</h2>
                <div className="h-px bg-white/20 flex-1"></div>
              </div>
              
              {/* Unified Chart */}
              <Card title="API Usage Overview" className="mb-6">
                <div className="flex flex-col">
                  {selectedViewApis.length > 0 && (
                    <div className="text-sm text-white/60 poppins-light mb-2">
                      {getMonthYear(new Date().getTime())}
                    </div>
                  )}
                  <div className="h-64 sm:h-80 2xl:h-[32rem] mb-6">
                    {selectedViewApis.length > 0 ? (
                      <Bar
                        key={`chart-${apiKeysToUse.join('-')}-${selectedViewApis.join('-')}`}
                        data={(() => {
                          const apiUsageMap = {
                            maps: mapsUsage,
                            search: searchUsage,
                            geocode: geocodeUsage,
                            directions: directionsUsage,
                            mapmatching: mapMatchingUsage,
                            optimize: optimizeUsage,
                            matrix: matrixUsage,
                            isochrone: isochroneUsage,
                            elevation: elevationUsage,
                            navigation: navigationUsage,
                            tiles: tilesUsage,
                            staticImages: staticImagesUsage
                          };

                          const apiColorMap = {
                            maps: 'rgba(59, 130, 246, 0.8)',
                            search: 'rgba(16, 185, 129, 0.8)',
                            geocode: 'rgba(245, 101, 101, 0.8)',
                            directions: 'rgba(139, 92, 246, 0.8)',
                            mapmatching: 'rgba(251, 191, 36, 0.8)',
                            optimize: 'rgba(236, 72, 153, 0.8)',
                            matrix: 'rgba(34, 197, 94, 0.8)',
                            isochrone: 'rgba(168, 85, 247, 0.8)',
                            elevation: 'rgba(255, 159, 64, 0.8)',
                            navigation: 'rgba(201, 203, 207, 0.8)',
                            tiles: 'rgba(102, 187, 106, 0.8)',
                            staticImages: 'rgba(233, 30, 99, 0.8)'
                          };

                          const apiLabelMap = {
                            maps: 'Maps API',
                            search: 'Search API',
                            geocode: 'Geocoding API',
                            directions: 'Directions API',
                            mapmatching: 'Map Matching API',
                            optimize: 'Optimize API',
                            matrix: 'Matrix API',
                            isochrone: 'Isochrone API',
                            elevation: 'Elevation API',
                            navigation: 'Navigation SDK',
                            tiles: 'Tiles API',
                            staticImages: 'Static Images API'
                          };

                          let allLabels = [];
                          const datasets = [];

                          selectedViewApis.forEach(apiId => {
                            const usage = apiUsageMap[apiId];
                            if (usage?.data?.usageHistory?.labels) {
                              if (allLabels.length === 0) {
                                allLabels = usage.data.usageHistory.labels.map(ts => {
                                  const date = new Date(ts);
                                  return `${date.toLocaleString('en-US', { month: 'short' })} ${date.getDate()}`;
                                });
                              }

                              const coloredData = applyApiKeyColors(
                                usage.data.usageHistory,
                                apiKeysToUse
                              );

                              // Always aggregate all datasets into one (sum all selected/all keys)
                              if (coloredData.datasets.length > 0) {
                                const aggregatedData = coloredData.datasets[0].data.map((_, index) => {
                                  return coloredData.datasets.reduce((sum, ds) => sum + (ds.data[index] || 0), 0);
                                });

                                // Only add dataset if it has non-zero values
                                const hasData = aggregatedData.some(value => value > 0);
                                if (hasData) {
                                  datasets.push({
                                    label: apiLabelMap[apiId],
                                    data: aggregatedData,
                                    backgroundColor: apiColorMap[apiId],
                                    borderColor: apiColorMap[apiId],
                                    borderWidth: 2,
                                    barThickness: 18
                                  });
                                }
                              }
                            }
                          });

                          return {
                            labels: allLabels,
                            datasets: datasets
                          };
                        })()
                      }
                        options={{
                          ...chartOptions,
                          plugins: {
                            ...chartOptions.plugins,
                            legend: { display: false }
                          }
                        }}
                        plugins={[roundedCornersPlugin]}
                      />
                    ) : (
                      <div className="flex items-center justify-center h-full">
                        <div className="text-center">
                          <div className="text-white/40 text-lg mb-2">📊</div>
                          <p className="text-white/60 poppins-light">Select APIs above to view usage analytics</p>
                        </div>
                      </div>
                    )}
                  </div>
                  
                  {/* Custom Glassmorphic Legend */}
                  <div className="mt-4 flex flex-wrap gap-3 justify-center">
                    {[
                      { id: 'maps', label: 'Maps API', color: 'rgba(59, 130, 246, 0.8)' },
                      { id: 'search', label: 'Search API', color: 'rgba(16, 185, 129, 0.8)' },
                      { id: 'geocode', label: 'Geocoding API', color: 'rgba(245, 101, 101, 0.8)' },
                      { id: 'directions', label: 'Directions API', color: 'rgba(139, 92, 246, 0.8)' },
                      { id: 'mapmatching', label: 'Map Matching API', color: 'rgba(251, 191, 36, 0.8)' },
                      { id: 'optimize', label: 'Optimize API', color: 'rgba(236, 72, 153, 0.8)' },
                      { id: 'matrix', label: 'Matrix API', color: 'rgba(34, 197, 94, 0.8)' },
                      { id: 'isochrone', label: 'Isochrone API', color: 'rgba(168, 85, 247, 0.8)' },
                      { id: 'elevation', label: 'Elevation API', color: 'rgba(255, 159, 64, 0.8)' },
                      { id: 'navigation', label: 'Navigation SDK', color: 'rgba(201, 203, 207, 0.8)' }
                    ].map(api => {
                      const isVisible = selectedViewApis.includes(api.id);
                      return (
                        <div
                          key={api.id}
                          onClick={() => {
                            const newSelection = selectedViewApis.includes(api.id)
                              ? selectedViewApis.filter(id => id !== api.id)
                              : [...selectedViewApis, api.id];
                            setSelectedViewApis(newSelection);
                          }}
                          className={`flex items-center gap-2 px-3 py-2 glass-card dark:glass-card-dark rounded-lg border cursor-pointer transition-all duration-300 hover:scale-105 ${
                            isVisible ? 'border-white/10 opacity-100' : 'border-white/5 opacity-40'
                          }`}
                        >
                          <div
                            className="w-3 h-3 rounded-full transition-all duration-300"
                            style={{
                              backgroundColor: isVisible ? api.color : 'transparent',
                              border: `2px solid ${api.color}`,
                              boxShadow: isVisible ? `0 0 8px ${api.color}` : 'none'
                            }}
                          />
                          <span className={`text-sm font-light poppins-light transition-colors duration-300 ${
                            isVisible ? 'text-white/80' : 'text-white/40'
                          }`}>
                            {api.label}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </Card>
              
              {/* API Statistics Table */}
              <Card title="API Usage Statistics" className="mb-6">
                <div className="overflow-x-hidden">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-white/10">
                        <th className="text-left py-3 px-4 text-white/80 poppins-medium text-sm">API</th>
                        <th className="text-right py-3 px-4 text-white/80 poppins-medium text-sm">Total Requests</th>
                        <th className="text-right py-3 px-4 text-white/80 poppins-medium text-sm">Cost</th>
                        <th className="text-right py-3 px-4 text-white/80 poppins-medium text-sm">Free Tier Usage</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[
                        {
                          name: 'Maps API',
                          usage: mapsUsage,
                          formatter: formatPricingMaps,
                          calculator: calculateProgressMaps,
                          color: 'rgba(59, 130, 246, 0.8)'
                        },
                        {
                          name: 'Search API',
                          usage: searchUsage,
                          formatter: formatPricingSearch,
                          calculator: calculateProgressAutocomplete,
                          color: 'rgba(16, 185, 129, 0.8)'
                        },
                        {
                          name: 'Geocoding API',
                          usage: geocodeUsage,
                          formatter: formatPricing3,
                          calculator: calculateProgress3,
                          color: 'rgba(245, 101, 101, 0.8)'
                        },
                        {
                          name: 'Directions API',
                          usage: directionsUsage,
                          formatter: formatPricingDirections,
                          calculator: calculateProgressDirections,
                          color: 'rgba(139, 92, 246, 0.8)'
                        },
                        {
                          name: 'Map Matching API',
                          usage: mapMatchingUsage,
                          formatter: formatPricingMapMatching,
                          calculator: calculateProgressMapMatching,
                          color: 'rgba(251, 191, 36, 0.8)'
                        },
                        {
                          name: 'Optimize API',
                          usage: optimizeUsage,
                          formatter: formatPricingOptimize,
                          calculator: calculateProgressOptimize,
                          color: 'rgba(236, 72, 153, 0.8)'
                        },
                        {
                          name: 'Matrix API',
                          usage: matrixUsage,
                          formatter: formatPricingMatrix,
                          calculator: calculateProgressMatrix,
                          color: 'rgba(34, 197, 94, 0.8)'
                        },
                        {
                          name: 'Isochrone API',
                          usage: isochroneUsage,
                          formatter: formatPricingIsochrone,
                          calculator: calculateProgressIsochrone,
                          color: 'rgba(168, 85, 247, 0.8)'
                        },
                        {
                          name: 'Navigation SDK',
                          usage: navigationUsage,
                          formatter: formatPricingNavigation,
                          calculator: calculateProgressNavigation,
                          color: 'rgba(201, 203, 207, 0.8)'
                        }
                      ].map((api, index) => (
                        <tr key={index} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                          <td className="py-3 px-4">
                            <div className="flex items-center gap-3">
                              <div 
                                className="w-3 h-3 rounded-full" 
                                style={{
                                  backgroundColor: api.color,
                                  boxShadow: `0 0 8px ${api.color}`
                                }}
                              />
                              <span className="text-white/90 poppins-light text-sm">{api.name}</span>
                            </div>
                          </td>
                          <td className="py-3 px-4 text-right">
                            <span className="text-white/80 poppins-light text-sm">
                              {api.usage?.loading ? 'Loading...' : (api.usage?.data?.totalUsage?.toLocaleString() || '0')}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-right">
                            <span className="text-white/90 poppins-medium text-sm">
                              {api.usage?.loading ? 'Loading...' : api.formatter(api.usage?.data?.totalUsage)}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-right">
                            <div className="flex items-center justify-end gap-2">
                              <div className="w-16 h-1.5 bg-white/20 rounded-full overflow-hidden">
                                <div
                                  className="h-full rounded-full transition-all duration-300"
                                  style={{
                                    backgroundColor: api.color,
                                    width: `${api.usage?.data?.totalUsage ? api.calculator(api.usage.data.totalUsage) : 0}%`
                                  }}
                                />
                              </div>
                              <span className="text-xs text-white/60 poppins-light min-w-[3rem] text-right">
                                {api.usage?.data?.totalUsage ? `${api.calculator(api.usage.data.totalUsage).toFixed(1)}%` : '0%'}
                              </span>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
              </div>
            </div>
          
          <div className="w-full 2xl:w-3/12 mt-6 2xl:mt-0 overflow-x-hidden min-w-0">
            {/* API Keys Selection */}
            <Card title="API Keys" className="sticky top-6 glass-card dark:glass-card-dark backdrop-blur-lg border border-white/10">
              <div className="space-y-3">
                {keys.loading ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-400"></div>
                  </div>
                ) : (
                  <>
                    <div className="flex items-center justify-between mb-4">
                      <span className="text-sm font-medium text-white/80 poppins-light">
                        Select API Keys
                      </span>
                      <div className="flex items-center gap-2">
                        {selectedApiKeys.length > 0 && (
                          <button
                            onClick={() => setSelectedApiKeys([])}
                            className="text-xs text-blue-400 hover:text-blue-300 poppins-light"
                          >
                            Clear All
                          </button>
                        )}
                        <div className="text-xs text-white/60 poppins-light">
                          {selectedApiKeys.length}/{MAX_SELECTED_KEYS}
                        </div>
                      </div>
                    </div>
                    <div className="space-y-2 max-h-96 overflow-y-auto">
                      {keys.data?.map((key) => (
                        <div
                          key={key._id}
                          className={`flex items-center p-3 rounded-lg border transition-all duration-200 ${
                            selectedApiKeys.includes(key.id)
                              ? 'bg-blue-500/20 border-blue-400/30 shadow-md backdrop-blur-sm'
                              : selectedApiKeys.length >= MAX_SELECTED_KEYS
                              ? 'bg-white/5 border-white/10 opacity-50 cursor-not-allowed backdrop-blur-sm'
                              : 'bg-white/5 border-white/10 hover:bg-white/10 hover:border-white/20 shadow-sm hover:shadow-md backdrop-blur-sm cursor-pointer'
                          }`}
                        >
                          <Checkbox
                            label={key.name}
                            name="analytics"
                            value={key.id}
                            onChange={(e) => handleApiKeyChange(key.id)}
                            checked={selectedApiKeys.includes(key.id)}
                            disabled={!selectedApiKeys.includes(key.id) && selectedApiKeys.length >= MAX_SELECTED_KEYS}
                            className="text-white poppins-light"
                          />
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </Card>
          </div>
        </Row>
          </div>
        </Animate>
      </div>
    </div>
  );
}